import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  reactQueryDefaultOptions,
  axiosGet,
  axiosFormPost,
  axiosPost,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

export const useQueryGetClientContacts = (clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.CLIENT_ADMIN_CONTACT_LIST(clientId));
  };
  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_CONTACT_LIST, clientId],
    getData,
    reactQueryDefaultOptions
  );
};

export const useQueryGetClientsMasterList = () => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.CLIENT_MASTER_LIST);
  };
  return useQuery(
    [QUERY_KEYS.CLIENT_MASTER_LIST],
    getData,
    reactQueryDefaultOptions
  );
};

export const useQueryGetClientBenefitGroups = (clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_BENEFITGROUOP_LIST(clientId)
    );
  };
  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_BENEFITGROUP_LIST, clientId],
    getData,
    reactQueryDefaultOptions
  );
};

export const useQueryGetClientBenefitPlans = (clientId, oePeriodId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_BENEFITPLANS_LIST(clientId, oePeriodId)
    );
  };

  let enabled = false;
  if (oePeriodId && clientId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: true,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_BENEFITPLAN_LIST, clientId, oePeriodId],
    getData,
    option
  );
};

export const useQueryGetClientBenefitRules = (clientId, planId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_BENEFITRULES_LIST(clientId, planId)
    );
  };
  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_BENEFITRULES_GET, clientId, planId],
    getData,
    reactQueryDefaultOptions
  );
};

export const useQueryGetClientNotificationHistory = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_NOTIFICATION_HISTORY(oePeriodId, clientId)
    );
  };

  let enabled = false;
  if (oePeriodId && clientId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: true,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_NOTIFICATION_HISTORY, oePeriodId, clientId],
    getData,
    option
  );
};

export const useQueryGetClientMenuDocuments = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_MENU_DOCUMENT(oePeriodId, clientId)
    );
  };

  let enabled = false;
  if (oePeriodId && clientId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: true,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_MENU_DOCUMENT, oePeriodId, clientId],
    getData,
    option
  );
};

export const useQueryGetAllClientMenuAll = (
  oePeriodId,
  clientId,
  enabledOverride
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_MENU_ALL(oePeriodId, clientId)
    );
  };

  const enabled =
    typeof enabledOverride === "boolean"
      ? enabledOverride
      : Boolean(oePeriodId && clientId);

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_MENU_All, oePeriodId, clientId],
    getData,
    option
  );
};

/**
 * Both dropdowns of the "Aetna Renewal to Multi Carrier Plan Comparison" section, as
 * { aetnaPlans, comparePlans }.
 *
 * Both lists contain only plans the client actually holds, and the API owns the
 * split by carrier, so the two can never overlap. The Aetna side is scoped to the
 * previous OE period, which the API resolves from the selected one - hence only the
 * selected period is passed here.
 *
 * `enabled` must be the caller's feature-gate result: the endpoint 404s for clients
 * without the "aetna" key, and gating the query keeps that request from being made
 * at all.
 */
export const useQueryGetAetnaComparePlans = (oePeriodId, clientId, enabled) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_AETNA_COMPARE_PLANS(oePeriodId, clientId)
    );
  };

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    enabled: Boolean(enabled) && Boolean(oePeriodId) && Boolean(clientId),
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_AETNA_COMPARE_PLANS, oePeriodId, clientId],
    getData,
    option
  );
};

/**
 * Both dropdowns of the "Model Based Aetna Renewal to Multi Carrier Comparison"
 * card: the same Aetna / non-Aetna split, but sourced from every rate model built
 * for the selected OE period rather than from the client's plan assignments - so a
 * plan added to a model that the client does not yet hold is offered here.
 *
 * Same shape and the same gate as useQueryGetAetnaComparePlans: `enabled` must be
 * the caller's feature-gate result, since this endpoint also 404s for clients
 * without the "aetna" key.
 */
export const useQueryGetAetnaCompareModelPlans = (oePeriodId, clientId, enabled) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_AETNA_COMPARE_MODEL_PLANS(oePeriodId, clientId)
    );
  };

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    enabled: Boolean(enabled) && Boolean(oePeriodId) && Boolean(clientId),
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_AETNA_COMPARE_MODEL_PLANS, oePeriodId, clientId],
    getData,
    option
  );
};

export const useQueryGetClientCurrentBenefitPlans = (clientId, offerTypeCode) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_CURRENT_BENEFIT_PLANS(clientId, offerTypeCode ?? "")
    );
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_CURRENT_BENEFIT_PLANS, clientId, offerTypeCode ?? ""],
    getData,
    {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      enabled: Boolean(clientId),
    }
  );
};

export const useQueryGetClientMenuBasePlans = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_MENU_BASE_ALL(oePeriodId, clientId)
    );
  };

  const enabled = Boolean(oePeriodId && clientId);

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_MENU_BASE_ALL, oePeriodId, clientId],
    getData,
    {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: false,
      enabled,
    }
  );
};

export const useQueryGetClientMenuFieldOverrides = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_MENU_FIELD_OVERRIDES(oePeriodId, clientId)
    );
  };

  const enabled = Boolean(oePeriodId && clientId);

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_MENU_FIELD_OVERRIDES, oePeriodId, clientId],
    getData,
    {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: false,
      enabled,
    }
  );
};

export const useMutateSaveClientMenuFieldOverrides = (oePeriodId, clientId) => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_ADMIN_MENU_FIELD_OVERRIDES_POST, data);
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_MENU_FIELD_OVERRIDES,
        oePeriodId,
        clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_MENU_All,
        oePeriodId,
        clientId,
      ]);
    },
  });
};

export const useQueryGetClientRateBandOverrides = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_RATE_BAND_OVERRIDES(oePeriodId, clientId)
    );
  };

  const enabled = Boolean(oePeriodId && clientId);

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_RATE_BAND_OVERRIDES, oePeriodId, clientId],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled,
    }
  );
};

export const useQueryGetClientRateBandOverrideHistory = (
  oePeriodId,
  clientId,
  benefitGroup = null,
  pageNumber = 1,
  pageSize = 20
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_RATE_BAND_OVERRIDES_HISTORY(
        oePeriodId,
        clientId,
        benefitGroup,
        pageNumber,
        pageSize
      )
    );
  };

  // History is fetched on demand when a benefit group's Audit dialog is opened.
  const enabled = Boolean(oePeriodId && clientId && benefitGroup);

  return useQuery(
    [
      QUERY_KEYS.CLIENT_ADMIN_RATE_BAND_OVERRIDES_HISTORY,
      oePeriodId,
      clientId,
      benefitGroup,
      pageNumber,
      pageSize,
    ],
    getData,
    {
      ...reactQueryDefaultOptions,
      enabled,
    }
  );
};

export const useMutateSaveClientRateBandOverride = (oePeriodId, clientId) => {
  const postData = async (data) => {
    return await axiosPost(
      SERVICE_URLS.CLIENT_ADMIN_RATE_BAND_OVERRIDES_POST,
      data
    );
  };

  const queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_RATE_BAND_OVERRIDES,
        oePeriodId,
        clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_RATE_BAND_OVERRIDES_HISTORY,
        oePeriodId,
        clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_BENEFITPLAN_LIST,
        clientId,
        oePeriodId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_MENU_DOCUMENT,
        oePeriodId,
        clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_MENU_All,
        oePeriodId,
        clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_ADMIN_MENU_BASE_ALL,
        oePeriodId,
        clientId,
      ]);
      // Saving an override now recalculates existing Contribution Model rows
      // (system and custom versions) on the backend. Invalidate without a
      // versionId so every cached model-detail/max-contribution query for
      // this client+period is refetched, regardless of which model version
      // the admin navigates to next.
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_DETAIL,
        oePeriodId,
        clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_MAXCONTRIBS,
        oePeriodId,
        clientId,
      ]);
    },
  });
};

export const useQueryGetEmpClientMenuDocuments = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_EMP_MENU_DOCUMENT(oePeriodId, clientId)
    );
  };

  let enabled = false;
  if (oePeriodId && clientId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: true,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_EMP_MENU_DOCUMENT, oePeriodId, clientId],
    getData,
    option
  );
};

export const useMutateUploadUserGuide = () => {
  const postData = async (data) => {
    var formData = new FormData();
    formData.append("data", data.file, data.fileName);

    return await axiosFormPost(SERVICE_URLS.USER_GUIDE_DOWNLOAD, formData);
  };

  return useMutation(postData, {
    onSuccess: () => {},
  });
};

export const useMutatePostEnableMaintenance = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_ADMIN_MAINTENANCE(), data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: () => {
      queryClient.invalidateQueries([QUERY_KEYS.CLIENT_ADMIN_MAINTENANCE]);
    },
  });
};


export const useQueryGetLatestMaintenanceLog = () => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_ADMIN_GET_LATEST_MAINTENANCE()
    );
  };

  let option = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: true,
    enabled: true,
  };

  return useQuery(
    [QUERY_KEYS.CLIENT_ADMIN_GET_LATEST_MAINTENANCE],
    getData,
    option
  );
};